import { FC, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  ContactDetailsParams,
  ContactProfileParams,
  GetUpdateContactModel,
} from "../../../../api/services/client/models";
import {
  AccordionHeader,
  AccordionHeaderText,
  MuiBox,
  MuiDoubleLabel,
  MuiDoubleLabelWithFormattedPhoneText,
  MuiGrid,
  MuiModal,
  MuiMultiSelect,
  MuiTypography,
} from "../../../../components";
import MuiChip from "../../../../components/chip/MuiChip";
import MuiEllipseLabel from "../../../../components/ellipseLabel/MuiEllipseLabel";
import { StatusBadge } from "../../../../components/statusBadge/StatusBadge";
import { MuiStyledTooltip } from "../../../../components/tooltip/MuiToolTip.style";
import {
  EMPTY_VALUE_DASH,
  MODULE_PERMISSIONS,
  PAGE_IDS,
  SYMBOL,
} from "../../../../helpers/constants";
import {
  getFullName,
  isEmpty,
  isNotEmpty,
  isRetailClient,
  isValueEmptyOrDash,
  replaceHashWithValue,
} from "../../../../helpers/utils";
import { useGridContainer } from "../../../../hooks/useGridContainer";
import { useMultiSelectARIALabels } from "../../../../hooks/useMultiSelectARIALabels";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { createClientSelector } from "../../../../redux/slices/client/createClientSelector";
import { COLORS } from "../../../../styles/constants.style";
import { ViewContactFormKey } from "../../modelsAndSchemas/Models";
import { createClientSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { ViewContactWrapper } from "./ViewContact.style";
import PermissionWrapper from "../../../../components/permissionWrapper/PermissionWrapper";

interface ViewContactProps {
  open: boolean;
  contactData?: GetUpdateContactModel;
  onClose: () => void;
  onClientEdit?: (contactId: string) => void;
  isAddButtonVisible?: boolean;
}

export const ViewContact: FC<ViewContactProps> = ({
  open,
  contactData,
  onClose,
  onClientEdit,
  isAddButtonVisible,
}) => {
  const {
    fetchLabel,
    commonLabels,
    fetchDropdownValues,
    fetchValueByKeyOfDropdown,
  } = useSiteCore();

  const { commonARIALabel } = useNavigationSiteCore();
  const multiSelectARIALabels = useMultiSelectARIALabels();

  const pageId = PAGE_IDS.VIEW_CONTACT;
  const { gridSizeMedium: gridSize } = useGridContainer(true);
  const [showToolTip, setShowToolTip] = useState(false);
  const clientNameRef = useRef<string>();
  const indexRef = useRef<number>();
  const clientData = useSelector(createClientSelector);

  const {
    addContactTitle,
    editContactButtonLabel,
    nameLabel,
    usedByClientHeading,
    addContactProfileHeading,
    contactTypeDropdown,
    surveyLanguageDropdown,
    prefixDropdown,
    jobTitleLabel,
    telePhoneLabel,
    mobileLabel,
    workEmailLabel,
    surveyLanguageLabel,
    statusLabel,
    contactDetailsHeading,
    contactTypeLabel,
    selectedContactTypesLabel,
    noUsedByClientAvailableLabel,
    selectedAsLabel,
    surveyContactLabel,
    viewContactTitle,
  } = createClientSiteCoreKeys;

  const getLabel = (key: string) => {
    return fetchLabel(addContactTitle, key);
  };

  const handleEditContact = () => {
    onClientEdit?.(contactData?.contactId ?? "");
  };

  const renderHeader = (label: string) => {
    return <AccordionHeaderText label={label} />;
  };

  const modalTitle = useMemo(() => {
    return contactData && isNotEmpty(contactData.contactProfile)
      ? `${replaceHashWithValue(getLabel(viewContactTitle), getFullName(contactData?.contactProfile.firstName, contactData?.contactProfile.lastName, true))}`
      : replaceHashWithValue(getLabel(viewContactTitle), "");
  }, [
    contactData?.contactProfile.lastName,
    contactData?.contactProfile.firstName,
  ]);

  const getContactProfileValue = (key: keyof ContactProfileParams) => {
    return contactData &&
      isNotEmpty(contactData?.contactProfile) &&
      isNotEmpty(contactData.contactProfile[key])
      ? (contactData.contactProfile[key] as string)
      : EMPTY_VALUE_DASH;
  };

  const getName = () => {
    const name = getFullName(
      getContactProfileValue("firstName"),
      getContactProfileValue("lastName"),
    );
    if (contactData?.contactProfile?.prefixId) {
      const prefix = fetchValueByKeyOfDropdown(
        addContactTitle,
        prefixDropdown,
        getContactProfileValue("prefixId"),
      );
      return `${prefix} ${name}`;
    }
    return name;
  };

  const getContactInfoValues = (key: keyof ContactProfileParams) => {
    const value = getContactProfileValue(key);
    return isValueEmptyOrDash(value) ? "" : value;
  };

  const getContactDetailsValue = (key: keyof ContactDetailsParams) => {
    if (contactData && isNotEmpty(contactData?.contactDetails)) {
      if (typeof contactData.contactDetails[key] === "boolean") {
        return contactData.contactDetails[key];
      } else if (contactData.contactDetails[key] instanceof Array) {
        return contactData?.contactDetails[key];
      }
    }
  };

  const getUsedByClientsData = () => {
    if (contactData && isNotEmpty(contactData?.contactDetails)) {
      return contactData.usedByClients ?? [];
    }
  };

  const handleShowToolTip = (clientName: string, index: number) => {
    clientNameRef.current = clientName;
    indexRef.current = index;
    setShowToolTip(true);
  };

  const handleHideToolTip = () => {
    clientNameRef.current = undefined;
    indexRef.current = undefined;
    setShowToolTip(false);
  };

  return (
    <MuiModal
      open={open}
      onClose={onClose}
      title={modalTitle}
      hasLargeContent
      showPrimaryButton={false}
      secondaryButtonTitle={commonLabels.close}
      tertiaryButtonTitle={
        isAddButtonVisible ? getLabel(editContactButtonLabel) : undefined
      }
      onSecondaryButtonClick={onClose}
      onTertiaryButtonClick={handleEditContact}
      stackButtonsOnMobile={true}
      className="view-contact-modal"
    >
      <ViewContactWrapper tabIndex={0}>
        <AccordionHeader
          title={renderHeader(getLabel(addContactProfileHeading))}
          showExpandCollapseIcon={false}
        >
          <MuiBox className="content">
            <MuiGrid container spacing={2}>
              <MuiGrid size={gridSize}>
                <MuiDoubleLabel
                  id={`${pageId}-${ViewContactFormKey.name}`}
                  label={getLabel(nameLabel)}
                  value={getName()}
                  isValueWithEllipsis
                />
              </MuiGrid>
              <MuiGrid size={gridSize}>
                <MuiDoubleLabel
                  id={`${pageId}-${ViewContactFormKey.workEmail}`}
                  label={getLabel(workEmailLabel)}
                  isValueWithEllipsis
                  value={getContactProfileValue("workEmail")}
                />
              </MuiGrid>
              <MuiGrid size={gridSize}>
                <MuiDoubleLabel
                  id={`${pageId}-${ViewContactFormKey.jobTitle}`}
                  label={getLabel(jobTitleLabel)}
                  value={getContactProfileValue("jobTitle")}
                />
              </MuiGrid>
              <MuiGrid size={gridSize}>
                <MuiDoubleLabelWithFormattedPhoneText
                  id={`${pageId}-${ViewContactFormKey.telephoneNumber}`}
                  label={getLabel(telePhoneLabel)}
                  replaceEmptyWithDash
                  selectedCountry={getContactInfoValues("telephoneISOCode")}
                  phoneNumber={getContactInfoValues("telephoneNumber")}
                  countryCode={getContactInfoValues("telephoneCountryCode")}
                />
              </MuiGrid>
              <MuiGrid size={gridSize}>
                <MuiDoubleLabelWithFormattedPhoneText
                  id={`${pageId}-${ViewContactFormKey.mobileNumber}`}
                  label={getLabel(mobileLabel)}
                  replaceEmptyWithDash={true}
                  selectedCountry={getContactInfoValues("mobileISOCode")}
                  phoneNumber={getContactInfoValues("mobileNumber")}
                  countryCode={getContactInfoValues("mobileCountryCode")}
                />
              </MuiGrid>

              <MuiGrid size={gridSize}>
                <MuiDoubleLabel
                  id={`${pageId}-${ViewContactFormKey.surveyLanguage}`}
                  label={getLabel(surveyLanguageLabel)}
                  value={fetchValueByKeyOfDropdown(
                    addContactTitle,
                    surveyLanguageDropdown,
                    getContactProfileValue("surveyLanguageId"),
                  )}
                />
              </MuiGrid>
              <MuiGrid size={gridSize}>
                <MuiDoubleLabel
                  id={`${pageId}-${ViewContactFormKey.status}`}
                  label={getLabel(statusLabel)}
                  value={
                    <StatusBadge
                      status={getContactProfileValue("status") as any}
                      label={fetchValueByKeyOfDropdown(
                        createClientSiteCoreKeys.contactsTitle,
                        createClientSiteCoreKeys.contactStatusList,
                        getContactProfileValue("status"),
                      )}
                    />
                  }
                />
              </MuiGrid>
            </MuiGrid>
          </MuiBox>
        </AccordionHeader>
        {!isRetailClient(clientData.clientTypeId) && (
          <AccordionHeader
            title={renderHeader(getLabel(usedByClientHeading))}
            showExpandCollapseIcon={false}
          >
            <MuiBox className="content">
              <MuiGrid
                container
                spacing={2}
                className={`${isEmpty(getUsedByClientsData()) ? "align-center" : ""}`}
              >
                {isNotEmpty(getUsedByClientsData()) ? (
                  getUsedByClientsData()?.map((client, index) => {
                    return (
                      <MuiGrid
                        size={gridSize}
                        key={`${client.clientCode}-${index}`}
                      >
                        <PermissionWrapper
                          permission={
                            MODULE_PERMISSIONS.VIEW_CLIENT_PARENT_CONTACTS
                          }
                        >
                          <MuiStyledTooltip
                            open={showToolTip && index === indexRef.current}
                            placement="top"
                            id={`${pageId}-${client.clientCode}-tooltip`}
                            toolTipBgColor={COLORS.cirrus_black8}
                            textColor={COLORS.cirrus_white}
                            title={
                              <MuiTypography variant="h3">
                                {clientNameRef.current}
                              </MuiTypography>
                            }
                          >
                            <MuiBox className="used-by-client-details">
                              <MuiTypography
                                component={"h4"}
                                data-testid={`${pageId}-${client.clientName}`}
                                variant="h4Bold"
                                className="client-name-text"
                                onMouseEnter={() =>
                                  handleShowToolTip(client.clientName, index)
                                }
                                onMouseLeave={handleHideToolTip}
                              >
                                {client.clientName}
                              </MuiTypography>
                              <MuiBox className="parent-tag-container">
                                <MuiEllipseLabel
                                  data-testid={`${pageId}-${client.countryName}-country`}
                                  wrapperClassName={`${client.isParent ? "width-75" : "full-width"}`}
                                  labelTypoVariant="h3"
                                  labelTypoClassName="sub-text"
                                  text={`${client.countryName} - ${client.clientCode} ${client.isParent ? SYMBOL.PIPE : ""}`}
                                />
                                {client.isParent && (
                                  <MuiBox className="parent-tag">
                                    <MuiTypography
                                      variant="h3Bold"
                                      className="parent-text"
                                      data-testid={`${pageId}-${client.clientName}-parent`}
                                    >
                                      {commonLabels.parent}
                                    </MuiTypography>
                                  </MuiBox>
                                )}
                              </MuiBox>
                            </MuiBox>
                          </MuiStyledTooltip>
                        </PermissionWrapper>
                      </MuiGrid>
                    );
                  })
                ) : (
                  <MuiTypography
                    variant="h3"
                    data-testid={`${pageId}-${ViewContactFormKey.noUsedByClient}`}
                  >
                    {getLabel(noUsedByClientAvailableLabel)}
                  </MuiTypography>
                )}
              </MuiGrid>
            </MuiBox>
          </AccordionHeader>
        )}
        <AccordionHeader
          title={renderHeader(getLabel(contactDetailsHeading))}
          showExpandCollapseIcon={false}
        >
          <MuiBox className="content">
            <MuiGrid container spacing={2} className="contact-type-container">
              <MuiGrid size={gridSize}>
                {isNotEmpty(getContactDetailsValue("contactTypeId")) ? (
                  <MuiMultiSelect
                    id={`${pageId}-${ViewContactFormKey.contactType}`}
                    label={getLabel(contactTypeLabel)}
                    className="contact-type-dropdown"
                    options={fetchDropdownValues(
                      addContactTitle,
                      contactTypeDropdown,
                    )}
                    showChip={true}
                    showDropdown={false}
                    isChipEditable={false}
                    dialogTitle={getLabel(selectedContactTypesLabel)}
                    dialogWidth={376}
                    selectedValues={
                      getContactDetailsValue("contactTypeId") as string[]
                    }
                    selectPlaceHolder={commonLabels.selectPlaceHolder}
                    searchPlaceHolder={commonLabels.search}
                    selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
                    doneText={commonLabels.done}
                    allARIAProps={multiSelectARIALabels}
                  />
                ) : (
                  <MuiDoubleLabel
                    label={getLabel(contactTypeLabel)}
                    value={EMPTY_VALUE_DASH}
                  />
                )}
              </MuiGrid>
              {getContactDetailsValue("isSurveyContact") && (
                <MuiGrid size={gridSize} className="checkbox-container">
                  <MuiTypography
                    data-testid={`${pageId}-${ViewContactFormKey.selected}`}
                    component={"p"}
                    variant="h3"
                  >
                    {getLabel(selectedAsLabel)}
                  </MuiTypography>
                  <MuiChip
                    data={[
                      {
                        label: getLabel(surveyContactLabel),
                        value: getLabel(surveyContactLabel),
                      },
                    ]}
                    isEditable={false}
                    handleDelete={() => {}}
                    showMoreARIALabel={commonARIALabel.showNMore}
                    removeChipNameARIALabel={commonARIALabel.removeChipName}
                  />
                </MuiGrid>
              )}
            </MuiGrid>
          </MuiBox>
        </AccordionHeader>
        <MuiBox />
      </ViewContactWrapper>
    </MuiModal>
  );
};
